/**
 * Bulk Basic-Salary Loader tab.
 *
 * SEPARATE from the generic employee importer. Reads ONLY identity columns
 * (Iqama / National ID / Employee Number) + a Salary column and writes ONLY
 * compensation.basic_salary.
 *
 * Flow: upload file -> dry-run (no-write preview, shows current vs file salary)
 *   -> on a passing dry-run an approval token is issued -> Execute (token-gated).
 * Write rules: empty/0 -> SET, equals -> NO_CHANGE, differs -> CONFLICT (never
 * overwritten), blank file -> SKIP_BLANK, no match -> NOT_FOUND.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import {
  Upload, Wallet, Loader2, Play, ShieldCheck, AlertTriangle, Download,
  RefreshCw, CheckCircle, XCircle, Search,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BATCH_REASON = 'Initial bulk basic-salary load 2026-06-30, approved by GM';

const RESULT_META = {
  set: { cls: 'bg-green-100 text-green-700 dark:bg-green-900/30', label: 'SET' },
  no_change: { cls: 'bg-gray-100 text-gray-600 dark:bg-gray-800', label: 'NO CHANGE' },
  conflict: { cls: 'bg-brand-brown/15 text-brand-brown', label: 'CONFLICT' },
  skip_blank: { cls: 'bg-gray-100 text-gray-500 dark:bg-gray-800', label: 'SKIP (BLANK)' },
  not_found: { cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30', label: 'NOT FOUND' },
  failed: { cls: 'bg-red-100 text-red-700 dark:bg-red-900/30', label: 'FAILED' },
};

const fmtSar = (v) =>
  v === null || v === undefined ? '—' : `${Number(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} SAR`;

export default function SalaryLoaderTab() {
  const { t } = useLanguage();
  const { token } = useAuth();

  const [file, setFile] = useState(null);
  const [dryRun, setDryRun] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [dryRunning, setDryRunning] = useState(false);
  const [executionResult, setExecutionResult] = useState(null);
  const [batches, setBatches] = useState([]);
  const [error, setError] = useState('');

  const loadBatches = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/import/salary/batches?limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setBatches(data.batches || []);
      }
    } catch (e) {
      console.error('Failed to load salary batches', e);
    }
  }, [token]);

  useEffect(() => { loadBatches(); }, [loadBatches]);

  const reset = () => {
    setFile(null);
    setDryRun(null);
    setExecutionResult(null);
    setError('');
  };

  const handleDryRun = async () => {
    if (!file) {
      setError(t('salarySelectFile', 'Select a CSV/XLSX file with identity columns and a Salary column.'));
      return;
    }
    setDryRunning(true);
    setError('');
    setExecutionResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/import/salary/dry-run`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) {
        const d = data?.detail || {};
        setError(d.error ? `${d.error}${d.missing ? ` (missing: ${d.missing.join(', ')})` : ''}` : t('salaryDryRunFailed', 'Dry-run failed.'));
        setDryRun(null);
        return;
      }
      setDryRun(data);
      loadBatches();
    } catch (e) {
      setError(e.message || t('salaryDryRunFailed', 'Dry-run failed.'));
    } finally {
      setDryRunning(false);
    }
  };

  const handleExecute = async () => {
    if (!dryRun?.approval_token) {
      setError(t('salaryApprovalRequired', 'Run a dry-run that has at least one SET row to approve this file before executing.'));
      return;
    }
    setExecuting(true);
    setError('');
    try {
      const res = await fetch(
        `${API_URL}/api/import/salary/execute?salary_batch_id=${dryRun.salary_batch_id}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'X-Import-Approval-Token': dryRun.approval_token,
          },
        },
      );
      const data = await res.json();
      if (!res.ok) {
        setError(data?.detail?.error || t('salaryExecuteFailed', 'Execute failed.'));
        return;
      }
      setExecutionResult(data);
      setDryRun(null);
      loadBatches();
    } catch (e) {
      setError(e.message || t('salaryExecuteFailed', 'Execute failed.'));
    } finally {
      setExecuting(false);
    }
  };

  const downloadSignoff = async (batchId, fileName, sha) => {
    try {
      const res = await fetch(`${API_URL}/api/import/salary/batches/${batchId}/signoff?fmt=md`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) { setError(t('signoffDownloadFailed', 'Could not download the sign-off sheet.')); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safe = (fileName || 'salary').replace(/\W+/g, '_');
      link.download = `salary_signoff_${safe}_${(sha || '').slice(0, 8)}.md`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message);
    }
  };

  const s = dryRun?.summary;
  const stats = s ? [
    { k: 'set', label: t('salaryWillSet', 'Will Set'), val: s.set_count, cls: 'text-green-600' },
    { k: 'no_change', label: t('salaryNoChange', 'No Change'), val: s.no_change, cls: 'text-gray-500' },
    { k: 'conflict', label: t('salaryConflict', 'Conflict'), val: s.conflict, cls: 'text-brand-brown' },
    { k: 'skip', label: t('salarySkip', 'Skipped'), val: s.skip_blank, cls: 'text-gray-400' },
    { k: 'not_found', label: t('salaryNotFound', 'Not Found'), val: s.not_found, cls: 'text-amber-600' },
    { k: 'failed', label: t('salaryFailed', 'Failed'), val: s.failed, cls: 'text-red-600' },
  ] : [];

  return (
    <div className="space-y-4" data-testid="salary-loader-tab">
      {error && (
        <Alert variant="destructive" data-testid="salary-error">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5 text-brand-orange" />
            {t('bulkSalaryLoader', 'Bulk Basic-Salary Loader')}
          </CardTitle>
          <CardDescription>
            {t('bulkSalaryLoaderHint',
              'Reads only the identity columns (Iqama / National ID / Employee Number) and the Salary column, and writes only Basic Salary. Matching priority: Iqama → National ID → Employee Number. Empty/0 is set from the file; a stored salary that differs is flagged as a CONFLICT and never overwritten.')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border border-brand-orange/30 bg-brand-orange/5 p-3 text-sm" data-testid="salary-reason-note">
            <ShieldCheck className="inline h-4 w-4 mr-1 text-brand-orange" />
            {t('salaryReasonNote', 'Every salary write is recorded with the protected-field reason:')}{' '}
            <span className="font-medium">“{BATCH_REASON}”</span>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <input
              type="file"
              accept=".csv,.xlsx"
              data-testid="salary-file-input"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setDryRun(null); setExecutionResult(null); setError(''); }}
              className="block text-sm file:mr-3 file:rounded-md file:border-0 file:bg-brand-black file:px-4 file:py-2 file:text-white hover:file:bg-brand-black/80"
            />
            <div className="flex gap-2">
              <Button onClick={handleDryRun} disabled={!file || dryRunning} data-testid="salary-dry-run-btn">
                {dryRunning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                {t('salaryRunDryRun', 'Run Dry-Run')}
              </Button>
              {(dryRun || executionResult) && (
                <Button variant="outline" onClick={reset} data-testid="salary-reset-btn">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {t('reset', 'Reset')}
                </Button>
              )}
            </div>
          </div>

          {executionResult && (
            <Alert data-testid="salary-execution-result" className="border-green-300 bg-green-50 dark:bg-green-900/10">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription>
                {t('salaryExecuted', 'Executed.')} {t('salaryWillSet', 'Set')}: <b>{executionResult.summary.set_count}</b>,{' '}
                {t('salaryNoChange', 'No Change')}: <b>{executionResult.summary.no_change}</b>,{' '}
                {t('salaryConflict', 'Conflict')}: <b>{executionResult.summary.conflict}</b>,{' '}
                {t('salaryNotFound', 'Not Found')}: <b>{executionResult.summary.not_found}</b>.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {dryRun && (
        <Card data-testid="salary-dry-run-report">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-brand-orange" />
              {t('salaryDryRunReport', 'Dry-Run Preview (no changes written)')}
            </CardTitle>
            <CardDescription className="font-mono text-xs break-all">
              SHA-256: {dryRun.file_sha256}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {stats.map((st) => (
                <div key={st.k} className="p-3 rounded-lg border text-center" data-testid={`salary-stat-${st.k}`}>
                  <div className={`text-2xl font-bold ${st.cls}`}>{st.val}</div>
                  <div className="text-xs text-muted-foreground">{st.label}</div>
                </div>
              ))}
            </div>

            <div
              className={`rounded-lg p-3 text-sm flex items-center gap-2 ${dryRun.can_execute ? 'bg-green-50 text-green-700 dark:bg-green-900/10' : 'bg-amber-50 text-amber-700 dark:bg-amber-900/10'}`}
              data-testid="salary-readiness"
            >
              {dryRun.can_execute
                ? <><CheckCircle className="h-4 w-4" /> {t('salaryReady', 'Ready to execute — an approval token is bound to this file.')}</>
                : <><XCircle className="h-4 w-4" /> {t('salaryNotReady', 'Nothing to set (no empty/0 salaries matched). Execution is not enabled.')}</>}
            </div>

            <div className="border rounded-lg overflow-hidden">
              <Table data-testid="salary-rows-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>{t('result', 'Result')}</TableHead>
                    <TableHead>{t('matchedBy', 'Matched By')}</TableHead>
                    <TableHead>{t('employee', 'Employee')}</TableHead>
                    <TableHead className="text-right">{t('salaryCurrent', 'Current')}</TableHead>
                    <TableHead className="text-right">{t('salaryFromFile', 'From File')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dryRun.outcomes.map((o) => {
                    const meta = RESULT_META[o.result] || { cls: '', label: o.result };
                    return (
                      <TableRow key={o.row_number} data-testid={`salary-row-${o.row_number}`}>
                        <TableCell className="text-muted-foreground">{o.row_number}</TableCell>
                        <TableCell><Badge className={meta.cls} data-testid={`salary-row-result-${o.row_number}`}>{meta.label}</Badge></TableCell>
                        <TableCell className="text-xs">{o.matched_by || '—'}</TableCell>
                        <TableCell className="text-sm">
                          {o.employee_name || '—'}
                          {o.employee_number ? <span className="text-muted-foreground"> ({o.employee_number})</span> : ''}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{fmtSar(o.current_salary)}</TableCell>
                        <TableCell className={`text-right tabular-nums ${o.result === 'conflict' ? 'text-brand-brown font-semibold' : ''}`}>{fmtSar(o.file_salary)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <div className="flex justify-end">
              <Button
                onClick={handleExecute}
                disabled={!dryRun.can_execute || executing}
                className="bg-brand-black hover:bg-brand-black/80"
                data-testid="salary-execute-btn"
              >
                {executing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
                {t('salaryExecute', 'Execute Salary Load')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* History / audit of salary loads */}
      <Card data-testid="salary-batches-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">{t('salaryLoadHistory', 'Salary Load History')}</CardTitle>
            <CardDescription>{t('salaryLoadHistoryHint', 'Who loaded which file (by SHA-256), when, status. Download the number-free sign-off.')}</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={loadBatches} data-testid="salary-refresh-batches-btn">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent>
          {batches.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground" data-testid="salary-batches-empty">
              {t('noSalaryBatches', 'No salary loads yet. A dry-run records a batch here.')}
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table data-testid="salary-batches-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('file', 'File')}</TableHead>
                    <TableHead>{t('status', 'Status')}</TableHead>
                    <TableHead>{t('uploadedBy', 'By')}</TableHead>
                    <TableHead className="text-center">{t('rows', 'Rows')}</TableHead>
                    <TableHead className="text-right">{t('actions', 'Actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {batches.map((b) => (
                    <TableRow key={b.id} data-testid={`salary-batch-row-${b.id}`}>
                      <TableCell>
                        <div className="text-sm font-medium">{b.file_name}</div>
                        <div className="text-[10px] font-mono text-muted-foreground break-all">{(b.file_hash || '').slice(0, 16)}…</div>
                      </TableCell>
                      <TableCell>
                        {b.status === 'executed'
                          ? <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30">{t('executed', 'Executed')}</Badge>
                          : <Badge variant="outline">{t('pending', 'Pending')}</Badge>}
                      </TableCell>
                      <TableCell className="text-xs">{b.uploaded_by_email || b.uploaded_by}</TableCell>
                      <TableCell className="text-center">{b.total_rows}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="outline" size="sm" onClick={() => downloadSignoff(b.id, b.file_name, b.file_hash)} data-testid={`salary-signoff-${b.id}`}>
                          <Download className="h-4 w-4 mr-2" />
                          {t('signoffSheet', 'Sign-off')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
